import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getCurrentRegion } from '../helpers/siteHelpers';
import { resources, namespaces, defaultNS } from './resources.generated';

/**
 * Admin UI internationalization.
 *
 * - English (`en`) is the source language and the fallback for every key.
 * - Japanese (`ja`) is the first non-English locale. Untranslated keys (empty
 *   string in the ja JSON) fall back to English — never blank — because of
 *   `returnEmptyString: false` + `fallbackLng: 'en'`.
 * - German (`de`) is written for the Germany office's bench (grading-alignment
 *   Q8) and is a DRAFT awaiting HQ review (see TRANSLATION-REVIEW-de.md). Until
 *   HQ signs it off, no region selects it automatically: a grader picks it in
 *   the language switcher. Empty de values fall back to English, like ja.
 * - The active language is resolved from the region (jp → ja, everything else
 *   → en) and can be overridden manually via the language switcher; the manual
 *   choice is persisted in localStorage.
 *
 * Adding strings: put English under the relevant namespace JSON in
 * src/i18n/locales/en/, run `yarn i18n:gen`, then use
 * `const { t } = useTranslation('<namespace>')` and `t('your.key')`.
 */

export const SUPPORTED_LANGUAGES = ['en', 'ja', 'de'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<AppLanguage, string> = {
  en: 'English',
  ja: '日本語',
  de: 'Deutsch',
};

const STORAGE_KEY = 'pcg-admin-lang';

/**
 * Map a region code to the admin UI language. jp → Japanese, else English.
 * `eu` (the Germany office) stays English until HQ has reviewed the German
 * draft; switching it is one line here once they have.
 */
export function regionToLanguage(region: string | null): AppLanguage {
  return region === 'jp' ? 'ja' : 'en';
}

function isSupported(value: unknown): value is AppLanguage {
  return (SUPPORTED_LANGUAGES as readonly unknown[]).includes(value);
}

/** Resolve the language to boot with: manual override (localStorage) wins, else region. */
export function getInitialLanguage(): AppLanguage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch {
    /* localStorage unavailable (private mode, SSR) — fall through to region. */
  }
  return regionToLanguage(getCurrentRegion());
}

/** Change the active language and persist the manual choice. */
export function setAppLanguage(lng: AppLanguage): void {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    /* ignore persistence failure */
  }
  void i18n.changeLanguage(lng);
}

i18n.use(initReactI18next).init({
  resources,
  ns: namespaces as unknown as string[],
  defaultNS,
  lng: getInitialLanguage(),
  fallbackLng: 'en',
  // Empty ja / de values are treated as missing so they fall back to English.
  returnEmptyString: false,
  interpolation: {
    // React already escapes values, so i18next must not double-escape.
    escapeValue: false,
  },
  react: {
    // Resources are bundled (no async backend) — Suspense is unnecessary.
    useSuspense: false,
  },
});

export default i18n;
